import React, { useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash-es';
import { useQueryParam } from 'react-resource-router';

import { OptionData } from '@atlaskit/smart-user-picker';

import { useAppData } from 'src/app/data';
import { PullRequestState } from 'src/components/types';

import Filter from '../components/filter';
import { PRFilterState, PRUserFilterType } from '../types';

export const FILTER_DEBOUNCE_INTERVAL = 250;

const StatefulWorkspacePullRequestFilter = (): JSX.Element => {
  const appData = useAppData();
  const [searchQuery, setSearchQuery] = useQueryParam('query');
  const [search, setSearch] = useState(searchQuery);
  const [projectUuid, setProjectUuid] = useQueryParam('project');
  const [prState, setPrState] = useQueryParam('state');
  const [prStateAllChecked, setPrStateAllChecked] = useState(false);
  const [prUserFilter, setPrUserFilter] = useQueryParam('user_filter');
  const [, setPage] = useQueryParam('page');
  const [author, setAuthor] = useQueryParam('author');

  const debounceSearch = useMemo(
    () => debounce(setSearchQuery, FILTER_DEBOUNCE_INTERVAL),
    [setSearchQuery]
  );

  const { user } = appData;

  const resetPage = () => {
    // Use `replace` instead of the default `push` to prevent an additional
    // update to the route after a separate query param is updated.
    // For now, `resetPage` should be called only when another query param is
    // modified.
    setPage(undefined, 'replace');
  };

  const onChangeSearch = (s: string) => {
    debounceSearch(s || undefined);
    setSearch(s);
    resetPage();
  };

  const onChangePrState = (
    states: PullRequestState[],
    statesAllChecked?: boolean
  ) => {
    setPrState(states.join('+') || undefined);
    setPrStateAllChecked(statesAllChecked || false);
    resetPage();
  };

  const onChangeProject = (uuid: string | undefined) => {
    setProjectUuid(uuid);
    resetPage();
  };

  const onChangeUserFilter = (p: PRUserFilterType) => {
    setPrUserFilter(p);
    resetPage();
  };

  // Updates search textbox after clicking "view open pull requests"
  // link in no filter results state
  useEffect(() => {
    if (!searchQuery) {
      setSearch(searchQuery);
    }
  }, [searchQuery]);

  const onChangeAuthor = (authors: OptionData[]) => {
    const ids = authors?.map((a: OptionData) => a.id);
    setAuthor(ids.join('+') || undefined);
    resetPage();
  };

  return (
    <Filter
      author={author!}
      user={user}
      search={search!}
      prState={prState as PRFilterState}
      prStateAllChecked={prStateAllChecked}
      projectUuid={projectUuid!}
      prUserFilter={prUserFilter as PRUserFilterType}
      onChangeSearch={onChangeSearch}
      onChangePrState={onChangePrState}
      onChangeUserFilter={onChangeUserFilter}
      onChangeAuthor={onChangeAuthor}
      onChangeProject={onChangeProject}
    />
  );
};

export default StatefulWorkspacePullRequestFilter;
